import React from 'react';
import { useTranslation } from 'react-i18next';
import { currencyUtils, dateFormatter } from '../../../utils';
import { currencyNameConverter } from '../../../utils/Currency/currencyNameConverter';

const stringToInteger = (numberString: any) => {
    if (numberString === null) {
        return -1;
    }

    const string = numberString.toString();
    const numberWithoutCommas = string.replace(/,/g, '');
    const number = parseFloat(numberWithoutCommas);

    if (isNaN(number)) {
        return 0;
    }

    return number;
};

function createSorter(property: string) {
    return (a: { [key: string]: any }, b: { [key: string]: any }): number => {
        return stringToInteger(a[property]) - stringToInteger(b[property]);
    };
}

const CasinoColumns = (activeCurrencyCode: string, isFiltered: boolean) => {
    const { t } = useTranslation();

    const columns: any[] = [
        {
            title: t('casino_report_from_date'),
            label: 'casino_report_from_date',
            dataIndex: 'from',
            key: 'from',
            render: (text: any) => <span>{dateFormatter.getFormattedForReportDate(text)}</span>,
            width: 165,
            fixed: true,
            sorter: (a: { from: Date | string }, b: { from: Date | string }): number => {
                return new Date(a.from).getTime() - new Date(b.from).getTime();
            },
        },
        {
            title: t('casino_report_to_date'),
            label: 'casino_report_to_date',
            dataIndex: 'to',
            key: 'to',
            render: (text: any) => <span>{dateFormatter.getFormattedForReportDate(text)}</span>,
            width: 165,
            fixed: true,
            sorter: (a: { to: Date | string }, b: { to: Date | string }): number => {
                return new Date(a.to).getTime() - new Date(b.to).getTime();
            },
        },
        {
            title: t('spins'),
            label: 'spins',
            dataIndex: 'spins',
            key: 'spins',
            width: 165,
            sorter: createSorter('spins'),
        },
        {
            title: t('currency_filter'),
            label: 'currency_filter',
            dataIndex: 'currencyId',
            key: 'currencyId',
            render: (text: any) =>
                text === 0
                    ? isFiltered
                        ? t('filtered')
                        : t('all_consolidated')
                    : currencyNameConverter.idToCode(text),
            width: 165,
            sorter: (a: any, b: any): any => {
                const aCurrencyCode =
                    currencyUtils.getCurrencyDataById(a.currencyId)?.code || 'all';
                const bCurrencyCode =
                    currencyUtils.getCurrencyDataById(b.currencyId)?.code || 'all';
                return aCurrencyCode.localeCompare(bCurrencyCode);
            },
        },
        {
            title: 'Round Type',
            label: 'Round Type',
            dataIndex: 'roundType',
            key: 'roundType',
            width: 165,
            render: (value: string) => {
                const map: Record<string, string> = {
                    regular: 'Regular round',
                    'free-round': 'Free round',
                    buy: 'Buy feature',
                    'awarded-feature': 'Awarded feature ',
                };
                return map[value] || value;
            },
            sorter: (a: any, b: any) => {
                const mapOrder: Record<string, number> = {
                    regular: 1,
                    free: 2,
                    buy_feature: 3,
                    awarded_feature: 4,
                };
                return (mapOrder[a.roundType] || 99) - (mapOrder[b.roundType] || 99);
            },
        },
        {
            title: () => (
                <span className="table-title__span">
                    {t('bet')}
                    <span>{activeCurrencyCode}</span>
                </span>
            ),
            label: 'bet',
            dataIndex: 'bet',
            key: 'bet',
            width: 165,
            sorter: createSorter('bet'),
        },
        {
            title: () => (
                <span className="table-title__span">
                    {t('win')}
                    <span>{activeCurrencyCode}</span>
                </span>
            ),
            label: 'win',
            dataIndex: 'win',
            key: 'win',
            width: 165,
            sorter: createSorter('win'),
        },
        {
            title: () => (
                <span className="table-title__span">
                    {t('net')}
                    <span>{activeCurrencyCode}</span>
                </span>
            ),
            label: 'net',
            dataIndex: 'net',
            key: 'net',
            width: 165,
            sorter: createSorter('net'),
        },
        {
            title: () => (
                <span className="table-title__span">
                    {t('rtp')}
                    <span>{'%'}</span>
                </span>
            ),
            label: 'rtp',
            dataIndex: 'rtp',
            key: 'rtp',
            width: 165,
            sorter: createSorter('rtp'),
        },
        {
            title: t('players'),
            label: 'players',
            dataIndex: 'players',
            key: 'players',
            width: 165,
            sorter: createSorter('players'),
        },
        {
            title: t('newPlayers'),
            label: 'newPlayers',
            dataIndex: 'newPlayers',
            key: 'newPlayers',
            width: 165,
            sorter: createSorter('newPlayers'),
        },
    ];

    return columns;
};

export default CasinoColumns;
